import { unwrap } from "@sagot/api-client";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { serviceKeys } from "../service-keys";
import { useServiceMutation, useServiceQuery } from "../use-service-query";
import { CONTACT_PAGE_SIZE, CONTACT_TICKETS_LIMIT, ORGANISATION_OPTIONS_LIMIT, type ContactChip } from "./contact-constants";
import type { ContactFilters, ContactPatch } from "./contact-types";

/** The server-side filters of one chip: pending and blocked are statuses, admins a role, unlinked "no organisation". */
function chipQuery(chip: ContactChip) {
  return {
    status: chip === "pending" ? ("pending_approval" as const) : chip === "blocked" ? ("blocked" as const) : null,
    org_role: chip === "admins" ? ("org_admin" as const) : null,
    unlinked: chip === "unlinked" ? true : undefined,
  };
}

/** `GET /v1/contacts`, newest first, with cursor pagination ("Load more"). */
export function useContactList(filters: ContactFilters) {
  const api = useApi();
  const ws = useWorkspaceId();
  return useInfiniteQuery({
    queryKey: serviceKeys.contactList(ws, { ...filters }),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      unwrap(
        api.GET("/v1/contacts", {
          params: { query: { q: filters.q || null, organisation_id: filters.organisationId, segment_id: filters.segmentId ?? null, ...chipQuery(filters.chip), cursor: pageParam, limit: CONTACT_PAGE_SIZE } },
          signal,
        }),
      ),
    getNextPageParam: (last) => last.next_cursor ?? null,
  });
}

/** How many contacts a chip holds (`total` of a one-row page), optionally inside one organisation. */
export function useContactCount(chip: ContactChip, organisationId: string | null = null, enabled = true) {
  const ws = useWorkspaceId();
  return useServiceQuery({
    queryKey: serviceKeys.contactList(ws, { count: true, chip, organisationId }),
    enabled,
    request: (api, signal) => api.GET("/v1/contacts", { params: { query: { organisation_id: organisationId, ...chipQuery(chip), limit: 1 } }, signal }),
  });
}

export function useContact(contactId: string | null) {
  const ws = useWorkspaceId();
  const id = contactId ?? "";
  return useServiceQuery({
    queryKey: serviceKeys.contact(ws, id),
    enabled: contactId !== null,
    request: (api, signal) => api.GET("/v1/contacts/{contact_id}", { params: { path: { contact_id: id } }, signal }),
  });
}

/** Organisations for the filter select (the largest page the server allows). */
export function useOrganisationOptions(enabled: boolean) {
  const ws = useWorkspaceId();
  return useServiceQuery({
    queryKey: serviceKeys.organisationList(ws, { options: true }),
    enabled,
    request: (api, signal) => api.GET("/v1/organisations", { params: { query: { limit: ORGANISATION_OPTIONS_LIMIT } }, signal }),
  });
}

/** The few latest tickets of one person (needs `tickets.view`). */
export function useContactTickets(contactId: string, enabled: boolean) {
  const ws = useWorkspaceId();
  return useServiceQuery({
    queryKey: serviceKeys.ticketList(ws, { requester_id: contactId, limit: CONTACT_TICKETS_LIMIT }),
    enabled,
    request: (api, signal) => api.GET("/v1/tickets", { params: { query: { requester_id: contactId, limit: CONTACT_TICKETS_LIMIT } }, signal }),
  });
}

export function useUpdateContact() {
  return useServiceMutation({
    request: (api, variables: { id: string; patch: ContactPatch }) =>
      api.PATCH("/v1/contacts/{contact_id}", { params: { path: { contact_id: variables.id } }, body: variables.patch }),
    invalidate: (ws) => [serviceKeys.contacts(ws), serviceKeys.organisations(ws)],
  });
}

export function useApproveContact() {
  return useServiceMutation({
    request: (api, id: string) => api.POST("/v1/contacts/{contact_id}/approve", { params: { path: { contact_id: id } } }),
    invalidate: (ws) => [serviceKeys.contacts(ws), serviceKeys.organisations(ws)],
  });
}

export function useRejectContact() {
  return useServiceMutation({
    request: (api, id: string) => api.POST("/v1/contacts/{contact_id}/reject", { params: { path: { contact_id: id } } }),
    invalidate: (ws) => [serviceKeys.contacts(ws), serviceKeys.organisations(ws)],
  });
}

/** Staff confirmed the person's identity by phone (`POST /v1/contacts/{id}/verify`, audited). */
export function useVerifyContact() {
  return useServiceMutation({
    request: (api, id: string) => api.POST("/v1/contacts/{contact_id}/verify", { params: { path: { contact_id: id } } }),
    invalidate: (ws) => [serviceKeys.contacts(ws)],
  });
}

export function useBlockContact() {
  return useServiceMutation({
    request: (api, id: string) => api.POST("/v1/contacts/{contact_id}/block", { params: { path: { contact_id: id } } }),
    invalidate: (ws) => [serviceKeys.contacts(ws)],
  });
}

export function useUnblockContact() {
  return useServiceMutation({
    request: (api, id: string) => api.POST("/v1/contacts/{contact_id}/unblock", { params: { path: { contact_id: id } } }),
    invalidate: (ws) => [serviceKeys.contacts(ws)],
  });
}
